import React from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { Pill } from '@els/els-react--pill';
import InnerHTML from '@Components/InnerHTML';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';
import { formatPriceValueHtml, formatPurityValue } from './SubstanceCommercialSupplierInfo.helpers';
import SubstanceCommercialSupplierInformationSupplierContent from './SubstanceCommercialSupplierInfoCellSupplier';

import './SubstanceCommercialSupplierSummaryRow.scss';

function SubstanceCommercialSupplierSummaryRow({
  group,
  isExpanded,
  onToggle,
  handledCasRegistryNumbers = '',
  databaseId = '',
  detailRowId,
  isProdSelectionEnabled = false,
  visibleColumnIds = [],
}) {
  const shouldShowColumn = (columnId) => visibleColumnIds.includes(columnId);
  const supplierName = sanitizeDiscard(String(group.supplierName || ''), { allowedTags: [] });
  const supplierLabelSuffix = supplierName ? ` for ${supplierName}` : '';
  const toggleLabel = `${isExpanded ? 'Collapse' : 'Expand'} supplier details${supplierLabelSuffix}`;

  return (
    <tr
      className={`rcs-commercial-group-row no-hover-background ${isExpanded ? 'is-expanded no-border-bottom' : ''}`}
      data-e2e={`supplier-group-${group.supplierId}`}>
      {isProdSelectionEnabled && shouldShowColumn('selected') && <td className="column-checkbox" />}
      <th scope="row" className="column-supplier">
        <div className="rcs-commercial-group-supplier">
          <ButtonWithIcon
            data-e2e={`commercial-supplier-toggle-${group.supplierId}`}
            type="link"
            iconOnly
            linkIconSize="xs"
            sprite={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            className="rcs-commercial-group-toggle"
            aria-expanded={isExpanded}
            aria-controls={isExpanded ? detailRowId : undefined}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(event);
            }}>
            {toggleLabel}
          </ButtonWithIcon>
          <div className={`rcs-commercial-group-supplier__content ${isExpanded ? 'is-expanded' : ''}`}>
            <SubstanceCommercialSupplierInformationSupplierContent
              supplierUrl={group.supplierUrl}
              supplierName={group.supplierName}
              suppliersProductNumber={group.summaryOffer?.suppliersProductNumber}
              rcProductName={group.summaryOffer?.rcProductName}
              rcSupplierId={group.supplierId}
              databaseId={databaseId}
              casRegistryNumbers={handledCasRegistryNumbers}
              disableSupplierLink
            />
          </div>
        </div>
      </th>
      {shouldShowColumn('purity') && (
        <td className="column-purity">
          <InnerHTML tag="span" html={formatPurityValue(group.summaryOffer)} />
        </td>
      )}
      {shouldShowColumn('size') && (
        <td className="column-size">
          <InnerHTML tag="span" html={`${group.summaryOffer?.packageSize || ''}`} />
        </td>
      )}
      {shouldShowColumn('price') && (
        <td className="column-price">
          <div className="rcs-commercial-price-cell">
            <InnerHTML tag="span" html={formatPriceValueHtml(group.summaryOffer)} />
            {group.hiddenOfferCount > 0 && (
              <Pill pillColor="n1" textColor="text" className="rcs-commercial-more-pill">
                {`+${group.hiddenOfferCount} More`}
              </Pill>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

SubstanceCommercialSupplierSummaryRow.propTypes = {
  group: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  handledCasRegistryNumbers: PropTypes.string,
  databaseId: PropTypes.string,
  detailRowId: PropTypes.string.isRequired,
  isProdSelectionEnabled: PropTypes.bool,
  visibleColumnIds: PropTypes.arrayOf(PropTypes.string),
};

export default SubstanceCommercialSupplierSummaryRow;
